import { firstWeekDay, getWeeksFromFirstDay, startOfDay } from '@/helpers/date';
import { capitalizeFirstLetter } from '@/helpers/strings';
import { Place, PlaceService } from '@/types/state/shared';
import moment from 'moment';
import 'moment-timezone';
import { Availability, FromErpOverview } from './availability.types';

/**
 * Pure week-anchor arithmetic for the PickTime container (MPA-2665).
 *
 * Everything here is ported from the legacy `WeekPicker` instance methods so the
 * new container navigates the same horizon the legacy picker does. No redux, no
 * router, no fetching — `usePickTime` owns all of that.
 *
 * The anchors are UTC-midnight ISO-Monday timestamps (`firstWeekDay` reads
 * `moment.utc`), which is the basis `bookActions.getAvailability` expects.
 */

/**
 * `helpers/date.js` is untyped and its `= false` parameter defaults make TS infer
 * `boolean` arguments, so the timestamp form the legacy picker relies on has to
 * be declared here.
 */
const isoMondayOf = firstWeekDay as (timestamp?: number | false) => number;

/** Ms in a week; the legacy picker shifts its anchor by this literal. */
export const WEEK_MS = 3600 * 24 * 7 * 1000;

/** The ISO-Monday anchor for a timestamp. */
export const getWeekStart = (timestamp?: number): number => isoMondayOf(timestamp);

/**
 * The ISO-Monday anchor for a date *string* — a `'YYYY-MM-DD'` day-key or an
 * offset-carrying ISO instant. The day is read as written (`parseZone`) rather
 * than re-expressed in the ambient default tz: `moment(dateString)` would resolve
 * a day-key to the place's midnight, which precedes UTC midnight and so snaps a
 * Monday back into the previous week.
 */
export const getWeekStartOfDate = (dateString: string): number =>
  getWeekStart(moment.utc(moment.parseZone(dateString).format('YYYY-MM-DD'), 'YYYY-MM-DD').valueOf());

/** Never fetch a week starting before today. */
export const prepDate = (date: number): number => (date < startOfDay() ? startOfDay() : date);

/**
 * `capacity` is served on `service.about` but is absent from the PlaceService zod
 * schema, so it has to be read through a narrow local shape.
 */
const seats = (service: PlaceService): number =>
  ((service?.about ?? {}) as { capacity?: number | null })?.capacity ?? 0;

/**
 * Whether the picked services are booked as a group booking, i.e. whether slots
 * carry a meaningful "spots left". Ported from `WeekPicker.allowsGroupBookings`:
 * every picked service must seat more than one, and a bundle books a single
 * seat regardless.
 */
export const allowsGroupBookings = (services: PlaceService[] = [], appliedBundle?: unknown): boolean =>
  services.length > 0 && services.every((service) => seats(service) > 1) && !appliedBundle;

/**
 * `bookAhead` is served on `place.about.book` but is absent from the Place zod
 * schema, so it has to be read through a narrow local shape.
 */
type PlaceBookAhead = { bookAhead?: number | null };

/**
 * The weekdays (0 = Sunday) the place's weekly schedule has opening hours on, or
 * `undefined` when it carries no usable schedule. The builders need this to tell
 * a closed day from a fully booked one: the ERP's `datesWithOpeningHours` lists
 * only days that still have a bookable slot, so a fully booked day drops out of
 * it exactly like a closed one.
 *
 * Closed reflects the place's schedule only: a day the chosen employee does not
 * work, or one outside a deal or the booking horizon, is unavailable but not
 * closed.
 */
export const getOpenWeekdays = (program: Place['program']): number[] | undefined => {
  if (!program || typeof program !== 'object') return undefined;
  const openWeekdays = Object.entries(program)
    .filter(([, ranges]) => ranges?.some(({ from, to }) => from >= 0 && to > from))
    .map(([weekday]) => Number(weekday));
  return openWeekdays.length ? openWeekdays : undefined;
};

/**
 * Whether the schedule keeps the place closed on a day ('YYYY-MM-DD'). Without a
 * schedule nothing is known to be closed.
 */
export const isClosedWeekday = (dayKey: string, openWeekdays?: number[]): boolean =>
  !!openWeekdays && !openWeekdays.includes(moment.utc(dayKey, 'YYYY-MM-DD').day());

/**
 * How many weeks ahead are bookable — the bound the next-week arrow honours.
 *
 * Legacy branches on whether an availability overview EXISTS, and when one
 * exists with no open days it keeps whatever count it previously held (it
 * mutates an instance field). A pure function cannot hold that, so this falls
 * through to the lastWorkingDays/bookAhead branch instead: it equals legacy on
 * first load (the count legacy would be "keeping" is the one that branch
 * produced) and can only differ once an earlier overview had already narrowed it.
 */
export const getWeeksCount = ({
  place,
  lastWorkingDays,
  overview,
  isSistaminuten,
}: {
  place: Place;
  lastWorkingDays?: { last?: number; bookAhead?: number };
  overview?: FromErpOverview;
  isSistaminuten?: boolean;
}): number => {
  // Ported from LastMinuteWeekPicker: the last-minute flow only ever sells the
  // coming 24h, so it holds a single week and gains a second one only when that
  // window already reaches into the next ISO week. It precedes both branches
  // below because it bounds the flow, not the place — a place whose overview or
  // bookAhead reaches months out still sells nothing beyond the window here.
  if (isSistaminuten) {
    const start = moment();
    return start.isoWeek() === start.clone().add(18, 'hours').isoWeek() ? 1 : 2;
  }

  const openDays = overview?.datesWithOpeningHours;
  if (openDays && openDays.length > 0) {
    return (
      Math.abs(
        moment()
          .startOf('isoWeek')
          .diff(moment(openDays[openDays.length - 1]), 'weeks'),
      ) + 1
    );
  }

  if (lastWorkingDays?.last && place?.source !== 2) {
    return getWeeksFromFirstDay(lastWorkingDays.last) + 1;
  }

  if (place?.source === 2) {
    // Legacy leaves the count undefined when a source-2 place carries no
    // bookAhead; a single week is the safe floor for a function that must
    // return a number.
    return lastWorkingDays?.bookAhead ?? 1;
  }

  return (place?.about?.book as PlaceBookAhead | undefined)?.bookAhead || 16;
};

/** Whether the previous-week arrow is live: the current week is the floor. */
export const canGoPrev = (weekStart: number): boolean => weekStart > isoMondayOf();

/** Whether the next-week arrow is live: the last bookable week is the ceiling. */
export const canGoNext = (weekStart: number, weeksCount: number): boolean =>
  weekStart < isoMondayOf() + (weeksCount - 1) * WEEK_MS;

/**
 * Terminal empty state: the fetch settled with no slots at all, the overview
 * names no future date, and we are still on the entry week — a visitor who
 * navigated past the horizon must see the empty week, not the terminal state.
 *
 * The entry-week conjunct is load-bearing, not cosmetic: the ERP computes the
 * overview from each fetch's anchor week, so off the entry week a response says
 * only "nothing from here on" and cannot answer for the horizon.
 */
export const isNoTimesAtAll = ({
  availability,
  hasSettled,
  weekStart,
}: {
  availability?: Availability;
  hasSettled: boolean;
  weekStart: number;
}): boolean => {
  if (!hasSettled || weekStart !== isoMondayOf()) return false;
  const slots = availability?.calendarTimeSlots;
  if (slots && Object.keys(slots).length > 0) return false;
  return !availability?.fromErpOverview?.firstAvailableDate;
};

/**
 * "D MMM - D MMM" for the 7 days from `weekStart`, localized + capitalized.
 * PickTimeLoading and PickTimeNoTimesAtAll take the range as a plain string and
 * render before any week model exists, so it cannot come from `model.days` the
 * way PickTimeWeekView derives its own title. Formatting matches that view.
 */
export const formatWeekRange = (weekStart: number, lang: string): string => {
  const start = moment(weekStart).startOf('day');
  const fmt = (input: moment.Moment) => capitalizeFirstLetter(input.clone().locale(lang).format('D MMM'));
  return `${fmt(start)} - ${fmt(start.clone().add(6, 'days'))}`;
};

/**
 * Folds a fresh overview into the one already pinned for the same query scope.
 *
 * The ERP recomputes the overview from each fetch's anchor week, so a later
 * week's response names only that window's open days rather than every day known
 * so far. Legacy unions the two (`WeekPicker.js:143-153`) and replaces wholesale
 * only when the window is unchanged.
 *
 * The window is identified by `maxDateChecked`, the bound that moves with the
 * anchor: `SERVICE_PROGRAM` carries the first response's `minDateChecked` onto
 * every later one (`bookReducer.ts:235-239`), so that bound cannot tell two
 * windows apart. A window whose bound is unknown counts as a new one — the pin
 * exists to hold a horizon, so it may not shrink on a comparison it cannot make.
 *
 * Open days come back in date order on every path, because `getWeeksCount` reads
 * the horizon off the last entry.
 *
 * Each response may be folded in only once: re-merging one already folded in
 * matches its own window and so replaces the union it just contributed to.
 */
export const mergeOverviews = (
  pinned: FromErpOverview | undefined,
  next: FromErpOverview | undefined,
): FromErpOverview | undefined => {
  if (!next) return pinned;
  const nextDates = [...(next.datesWithOpeningHours ?? [])].sort();
  if (!pinned) return { ...next, datesWithOpeningHours: nextDates };
  const isSameWindow = next.maxDateChecked != null && pinned.maxDateChecked === next.maxDateChecked;
  return {
    ...next,
    datesWithOpeningHours: isSameWindow
      ? nextDates
      : Array.from(new Set([...(pinned.datesWithOpeningHours ?? []), ...nextDates])).sort(),
  };
};

/**
 * A slot's wall-clock start relabelled as UTC — the time both `/change-booking-time` and the
 * checkout's picked hour take. Read in the slot's own offset rather than moment's ambient
 * default zone, which is the place's only while a pick-time container is mounted.
 */
export const toWallClockAsUtc = (start: string): moment.Moment => moment.parseZone(start).utc(true);
